import { Autocomplete, Avatar, Box, CircularProgress, TextField, Typography } from '@mui/material';
import type { AuthorDto } from 'common/interfaces/authors-api';
import type { IPageFormV2 } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { FC, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { authorsApiSlice } from 'store/api/authors-api/authors-api';

import { styles } from './author-select.styles';

interface AuthorSelectProps {
  disabled?: boolean;
}

export const AuthorSelect: FC<AuthorSelectProps> = ({ disabled }) => {
  const { control } = useFormContext<IPageFormV2>();
  const [inputValue, setInputValue] = useState('');
  const [searchParams, setSearchParams] = useState<{ firstn?: string; lastn?: string }>({});

  const { data, isFetching } = authorsApiSlice.useGetAuthorsQuery(searchParams);

  const authors: AuthorDto[] = data?.authors || [];

  const {
    field,
    fieldState: { error },
  } = useController({
    name: 'author',
    control,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue?.trim().length) {
        const [firstn, lastn] = inputValue.trim().split(' ');
        setSearchParams({ firstn, lastn });
      } else {
        setSearchParams({});
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const selectedAuthor = field.value?.id
    ? authors.find((a) => a.id === field.value.id) || field.value
    : null;

  return (
    <Autocomplete
      disabled={disabled}
      getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''}`.trim()}
      inputValue={inputValue}
      loading={isFetching}
      onChange={(_, newValue) =>
        field.onChange(
          newValue || {
            id: 0,
            first_name: '',
            last_name: '',
            gender: 'male',
            created_at: '',
            updated_at: '',
            info: {
              country: '',
              city: '',
              state: '',
              about: '',
              experience: { years: 0, specialization: '' },
            },
          },
        )
      }
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      options={authors}
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!error}
          helperText={error?.message}
          label="Author (Optional)"
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const initials = `${option.first_name?.[0] || ''}${
          option.last_name?.[0] || ''
        }`.toUpperCase();

        return (
          <Box component="li" {...props} sx={styles.authorOption}>
            <Avatar src={option.img} sx={{ width: 32, height: 32, fontSize: '0.9rem' }}>
              {initials}
            </Avatar>
            <Box sx={styles.authorInfo}>
              <Typography sx={styles.authorName}>{option.last_name}</Typography>
              <Typography sx={styles.authorFirstName}>{option.first_name}</Typography>
            </Box>
          </Box>
        );
      }}
      value={selectedAuthor}
    />
  );
};
